import React, { Component } from 'react';
import type { ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { convertErrorType } from 'utils/error-utils';
import { Text, VerticalLayout } from 'widgets';

import { addRumError } from '../datadog-logging';
import { FetcherError } from '../utils';

interface Props {
    resetKey: string;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    errorMessage: string;
}

class ErrorBoundaryClass extends Component<PropsWithChildren<Props>, State> {
    state = { hasError: false, errorMessage: '' };

    componentDidUpdate(prevProps: Props) {
        if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
            this.setState({
                hasError: false,
                errorMessage: '',
            });
        }
    }

    static getDerivedStateFromError(error: Error | FetcherError) {
        if (error instanceof FetcherError) {
            return {
                hasError: true,
                errorMessage: `${convertErrorType(error.errorType)} - ErrorID: #${error.errorId}`,
            };
        }

        return {
            hasError: true,
            errorMessage: 'ERROR: failed to render',
        };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (!(error instanceof FetcherError)) {
            addRumError(error, errorInfo);
        }
    }

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback !== undefined) return this.props.fallback;
            return (
                <VerticalLayout $padding="16px">
                    <Text $themeColor="negative">{this.state.errorMessage}</Text>
                </VerticalLayout>
            );
        }

        return this.props.children;
    }
}

export const ErrorBoundary = ({
    children,
    fallback,
    resetKey,
}: PropsWithChildren<{ fallback?: ReactNode; resetKey?: string }>) => {
    const location = useLocation();
    return (
        <ErrorBoundaryClass resetKey={resetKey ?? location.key} fallback={fallback}>
            {children}
        </ErrorBoundaryClass>
    );
};
